import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, type WorkOrder, type WorkOrderFilters } from '../../lib/api';
import { Card } from '../ui/Card';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { useAuthStore } from '../../stores/authStore';

/**
 * Configurable Workorder Deck View
 *
 * A reusable deck view component that can be configured for different workorder
 * filtered views like My Submitted, My Ready for Pickup, My Workorder Run, etc.
 */

export type WorkorderDeckConfig = {
  // View metadata
  title: string;
  icon: string;
  emptyMessage: string;
  emptyIcon: string;

  // Data filtering
  filterFn?: (workorders: WorkOrder[], userEmail: string) => WorkOrder[];
  apiFilters?: WorkOrderFilters;

  // Card display
  primaryField: keyof WorkOrder | ((wo: WorkOrder) => string);
  secondaryField?: keyof WorkOrder | ((wo: WorkOrder) => string);
  summaryField?: keyof WorkOrder | ((wo: WorkOrder) => string);
  showImage?: boolean;
  showUrgentBadge?: boolean;

  // Sorting
  sortFn?: (a: WorkOrder, b: WorkOrder) => number;

  // Grouping
  groupBy?: keyof WorkOrder | ((wo: WorkOrder) => string);
  groupLabel?: (value: string) => string;

  // Actions
  actions?: {
    name: string;
    label: string;
    variant: 'primary' | 'secondary' | 'ghost' | 'danger';
    onClick: (wo: WorkOrder) => void;
  }[];
  swipeRightAction?: string; // Action name for swipe right
};

interface WorkorderDeckViewProps {
  config: WorkorderDeckConfig;
  onSelect?: (workorder: WorkOrder) => void;
}

function formatAge(days: number | undefined): string {
  if (days === undefined || days === null) return '-';
  if (days === 0) return 'Today';
  if (days === 1) return '1 day';
  if (days < 7) return `${days} days`;
  if (days < 30) return `${Math.floor(days / 7)}w`;
  return `${Math.floor(days / 30)}mo`;
}

function getStatusLabel(status: string): string {
  // Extract the label after the number prefix (e.g., "1. Contact Made" → "Contact Made")
  const match = status.match(/^\d+\.\s*(.+)$/);
  if (match) return match[1];
  const statusMap: Record<string, string> = {}; // Fallback for unknown formats
  return statusMap[status] || status;
}

function getStatusVariant(status: string): 'default' | 'success' | 'warning' | 'danger' | 'info' | 'earth' {
  if (status.includes('Pending')) return 'warning';
  if (status.includes('Progress') || status.includes('Assigned')) return 'info';
  if (status.includes('Complete') || status.includes('Ready')) return 'success';
  if (status.includes('Declined')) return 'danger';
  return 'default';
}

function getFieldValue(
  wo: WorkOrder,
  field: keyof WorkOrder | ((wo: WorkOrder) => string) | undefined
): string {
  if (!field) return '';
  if (typeof field === 'function') return field(wo);
  const value = wo[field];
  return value != null ? String(value) : '';
}

export function WorkorderDeckView({ config, onSelect }: WorkorderDeckViewProps) {
  const [selectedWO, setSelectedWO] = useState<WorkOrder | null>(null);
  const { user } = useAuthStore();
  const userEmail = user?.email || '';

  // Fetch workorders
  const { data: workordersResponse, isLoading, error } = useQuery({
    queryKey: ['workorders', 'deck', config.title, config.apiFilters],
    queryFn: async () => {
      const result = await api.workorders.list({
        limit: 500,
        ...config.apiFilters,
      });
      return result;
    },
  });

  // Apply client-side filter and sort
  let workorders = workordersResponse?.data || [];

  if (config.filterFn) {
    workorders = config.filterFn(workorders, userEmail);
  }

  if (config.sortFn) {
    workorders = [...workorders].sort(config.sortFn);
  }

  // Group if configured
  const groups = config.groupBy
    ? groupWorkorders(workorders, config.groupBy, config.groupLabel)
    : null;

  const handleSelect = (wo: WorkOrder) => {
    setSelectedWO(wo);
    onSelect?.(wo);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin w-8 h-8 border-2 border-forest-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12 text-red-600">
        Failed to load workorders
      </div>
    );
  }

  if (workorders.length === 0) {
    return (
      <div className="text-center py-12 text-sage-500">
        <div className="text-4xl mb-4">{config.emptyIcon}</div>
        <p className="font-medium">{config.emptyMessage}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-xl">{config.icon}</span>
          <h2 className="text-lg font-semibold text-forest-900">{config.title}</h2>
          <Badge variant="default" size="sm">{workorders.length}</Badge>
        </div>
      </div>

      {/* Grouped or Flat List */}
      {groups ? (
        <div className="space-y-6">
          {groups.map((group) => (
            <div key={group.key}>
              <h3 className="text-sm font-medium text-sage-600 mb-3 px-1">
                {group.label} ({group.items.length})
              </h3>
              <div className="space-y-3">
                {group.items.map((wo) => (
                  <WorkorderCard
                    key={wo.id}
                    workorder={wo}
                    config={config}
                    onClick={() => handleSelect(wo)}
                    isSelected={selectedWO?.id === wo.id}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="space-y-3">
          {workorders.map((wo) => (
            <WorkorderCard
              key={wo.id}
              workorder={wo}
              config={config}
              onClick={() => handleSelect(wo)}
              isSelected={selectedWO?.id === wo.id}
            />
          ))}
        </div>
      )}

      {/* Detail Panel */}
      {selectedWO && (
        <Card className="mt-4 p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-forest-900">
              {getFieldValue(selectedWO, config.primaryField) || `WO #${selectedWO.id}`}
            </h3>
            <button
              onClick={() => setSelectedWO(null)}
              className="text-sage-400 hover:text-sage-600"
            >
              ✕
            </button>
          </div>
          <dl className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <dt className="text-sage-500">Client</dt>
              <dd className="text-forest-900">{selectedWO.clientName || '-'}</dd>
            </div>
            <div>
              <dt className="text-sage-500">Status</dt>
              <dd><Badge variant={getStatusVariant(selectedWO.status)}>{getStatusLabel(selectedWO.status)}</Badge></dd>
            </div>
            <div>
              <dt className="text-sage-500">Assigned To</dt>
              <dd className="text-forest-900">{selectedWO.assignedToName || '-'}</dd>
            </div>
            <div>
              <dt className="text-sage-500">Age</dt>
              <dd className="text-forest-900">{formatAge(selectedWO.ageDays)}</dd>
            </div>
            {selectedWO.location && (
              <div className="col-span-2">
                <dt className="text-sage-500">Location</dt>
                <dd className="text-forest-900">{selectedWO.location}</dd>
              </div>
            )}
            {selectedWO.notes && (
              <div className="col-span-2">
                <dt className="text-sage-500">Notes</dt>
                <dd className="text-forest-900">{selectedWO.notes}</dd>
              </div>
            )}
          </dl>
          {config.actions && config.actions.length > 0 && (
            <div className="flex gap-2 mt-4 pt-4 border-t border-sage-200">
              {config.actions.map((action) => (
                <Button
                  key={action.name}
                  variant={action.variant}
                  onClick={() => action.onClick(selectedWO)}
                >
                  {action.label}
                </Button>
              ))}
            </div>
          )}
        </Card>
      )}
    </div>
  );
}

// Card component
interface WorkorderCardProps {
  workorder: WorkOrder;
  config: WorkorderDeckConfig;
  onClick: () => void;
  isSelected?: boolean;
}

function WorkorderCard({ workorder, config, onClick, isSelected }: WorkorderCardProps) {
  const primaryValue = getFieldValue(workorder, config.primaryField);
  const secondaryValue = getFieldValue(workorder, config.secondaryField);
  const summaryValue = getFieldValue(workorder, config.summaryField);

  return (
    <Card
      className={`cursor-pointer transition-all ${
        isSelected
          ? 'ring-2 ring-forest-500 border-forest-500'
          : 'hover:shadow-md hover:border-sage-300'
      }`}
      onClick={onClick}
    >
      <div className="p-4">
        <div className="flex gap-4">
          {/* Image/Avatar */}
          {config.showImage && (
            <div className="w-12 h-12 flex-shrink-0 rounded-xl bg-sage-100 flex items-center justify-center overflow-hidden">
              {workorder.imageUrl ? (
                <img
                  src={workorder.imageUrl}
                  alt=""
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              ) : (
                <span className="text-sage-400 text-xl">📦</span>
              )}
            </div>
          )}

          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <h3 className="font-semibold text-forest-900 truncate">
                {primaryValue}
              </h3>
              <div className="flex items-center gap-1.5">
                {config.showUrgentBadge && workorder.urgent && (
                  <Badge variant="earth" size="sm">URGENT</Badge>
                )}
                <Badge variant={getStatusVariant(workorder.status)} size="sm">
                  {getStatusLabel(workorder.status)}
                </Badge>
              </div>
            </div>
            {secondaryValue && (
              <p className="text-sm text-sage-600 truncate">{secondaryValue}</p>
            )}
            {summaryValue && (
              <p className="text-xs text-sage-500 mt-1 truncate">{summaryValue}</p>
            )}
          </div>
        </div>

        {/* Quick Actions */}
        {config.actions && config.actions.length > 0 && (
          <div className="flex gap-2 mt-3 pt-3 border-t border-sage-100">
            {config.actions.slice(0, 3).map((action) => (
              <Button
                key={action.name}
                size="sm"
                variant={action.variant === 'primary' ? 'primary' : 'ghost'}
                onClick={(e) => {
                  e.stopPropagation();
                  action.onClick(workorder);
                }}
              >
                {action.label}
              </Button>
            ))}
          </div>
        )}
      </div>
    </Card>
  );
}

// Grouping helper
function groupWorkorders(
  workorders: WorkOrder[],
  groupBy: keyof WorkOrder | ((wo: WorkOrder) => string),
  labelFn?: (value: string) => string
): { key: string; label: string; items: WorkOrder[] }[] {
  const groups: Record<string, WorkOrder[]> = {};

  for (const wo of workorders) {
    const key = typeof groupBy === 'function' ? groupBy(wo) : String(wo[groupBy] || 'Other');
    if (!groups[key]) groups[key] = [];
    groups[key].push(wo);
  }

  return Object.entries(groups).map(([key, items]) => ({
    key,
    label: labelFn ? labelFn(key) : key,
    items,
  }));
}

// ============================================================================
// Pre-configured Views
// ============================================================================

/** My Submitted Workorders - Workorders submitted by current user */
export const MY_SUBMITTED_CONFIG: WorkorderDeckConfig = {
  title: 'My Submitted Workorders',
  icon: '📋',
  emptyMessage: "You haven't submitted any workorders",
  emptyIcon: '📭',
  filterFn: (workorders, userEmail) =>
    workorders.filter((wo) => wo.submittedBy === userEmail),
  apiFilters: { open: true },
  primaryField: 'detailedDescription',
  secondaryField: 'status',
  showImage: true,
  showUrgentBadge: true,
  groupBy: 'status',
  sortFn: (a, b) => {
    if (a.status !== b.status) return a.status.localeCompare(b.status);
    return (a.clientName || '').localeCompare(b.clientName || '');
  },
  actions: [
    { name: 'edit', label: 'Edit', variant: 'primary', onClick: (wo) => console.log('Edit:', wo.id) },
    { name: 'complete', label: 'Mark Complete', variant: 'ghost', onClick: (wo) => console.log('Complete:', wo.id) },
  ],
};

/** My Ready for Pickup - Workorders ready for pickup assigned to current user */
export const MY_READY_FOR_PICKUP_CONFIG: WorkorderDeckConfig = {
  title: 'Ready for Pickup',
  icon: '🚚',
  emptyMessage: 'No workorders ready for pickup',
  emptyIcon: '✅',
  filterFn: (workorders, userEmail) =>
    workorders.filter(
      (wo) =>
        wo.status.includes('Ready for Pickup') &&
        wo.assignedTo === userEmail
    ),
  apiFilters: { open: true },
  primaryField: 'detailedDescription',
  secondaryField: 'clientName',
  summaryField: (wo) => wo.location || 'No location',
  showImage: true,
  sortFn: (a, b) => (a.clientName || '').localeCompare(b.clientName || ''),
  actions: [
    { name: 'complete', label: 'Mark Complete', variant: 'primary', onClick: (wo) => console.log('Complete:', wo.id) },
    { name: 'view', label: 'View Client', variant: 'ghost', onClick: (wo) => console.log('View Client:', wo.clientId) },
  ],
};

/** My Workorder Run - Workorders assigned to current user for today's run */
export const MY_WORKORDER_RUN_CONFIG: WorkorderDeckConfig = {
  title: 'My Workorder Run',
  icon: '🎯',
  emptyMessage: 'No workorders in your run today',
  emptyIcon: '🎉',
  filterFn: (workorders, userEmail) =>
    workorders.filter(
      (wo) =>
        wo.assignedTo === userEmail &&
        wo.woRun && // Has run assignment
        !wo.status.includes('Complete') &&
        !wo.status.includes('Declined')
    ),
  apiFilters: { open: true },
  primaryField: 'detailedDescription',
  secondaryField: (wo) => `${wo.clientName || 'Unknown'} - ${wo.location || 'N/A'}`,
  summaryField: (wo) => `Run Order: ${wo.woRun || 'Not set'}`,
  showImage: true,
  showUrgentBadge: true,
  sortFn: (a, b) => (a.woRun || '').localeCompare(b.woRun || ''),
  actions: [
    { name: 'complete', label: 'Mark Complete', variant: 'primary', onClick: (wo) => console.log('Complete:', wo.id) },
    { name: 'view', label: 'View Client', variant: 'ghost', onClick: (wo) => console.log('View Client:', wo.clientId) },
    { name: 'map', label: 'Navigate', variant: 'ghost', onClick: (wo) => console.log('Navigate:', wo.id) },
  ],
};

/** My Assigned Workorders - All workorders assigned to current user */
export const MY_ASSIGNED_CONFIG: WorkorderDeckConfig = {
  title: 'My Assigned Workorders',
  icon: '👤',
  emptyMessage: 'No workorders assigned to you',
  emptyIcon: '📭',
  filterFn: (workorders, userEmail) =>
    workorders.filter((wo) => wo.assignedTo === userEmail),
  apiFilters: { open: true },
  primaryField: 'detailedDescription',
  secondaryField: 'status',
  showUrgentBadge: true,
  sortFn: (a, b) => {
    // Sort: status descending, then client ascending
    if (a.status !== b.status) return b.status.localeCompare(a.status);
    return (a.clientName || '').localeCompare(b.clientName || '');
  },
  actions: [
    { name: 'edit', label: 'Edit', variant: 'primary', onClick: (wo) => console.log('Edit:', wo.id) },
    { name: 'complete', label: 'Mark Complete', variant: 'ghost', onClick: (wo) => console.log('Complete:', wo.id) },
  ],
};
